/**
 * @license Copyright (c) 2014-2023, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

import { ClassicEditor } from '@ckeditor/ckeditor5-editor-classic';

import { UploadAdapter } from '@ckeditor/ckeditor5-adapter-ckfinder';
import { Alignment } from '@ckeditor/ckeditor5-alignment';
import { Autoformat } from '@ckeditor/ckeditor5-autoformat';
import { Autosave } from '@ckeditor/ckeditor5-autosave';
import {
	Bold,
	Code,
	Italic,
	Strikethrough,
	Subscript,
	Superscript,
	Underline
} from '@ckeditor/ckeditor5-basic-styles';
import { BlockQuote } from '@ckeditor/ckeditor5-block-quote';
import { CKFinder } from '@ckeditor/ckeditor5-ckfinder';
import { CloudServices } from '@ckeditor/ckeditor5-cloud-services';
import { CodeBlock } from '@ckeditor/ckeditor5-code-block';
// import { TableOfContents } from '@ckeditor/ckeditor5-document-outline';
import { Essentials } from '@ckeditor/ckeditor5-essentials';
import { FindAndReplace } from '@ckeditor/ckeditor5-find-and-replace';
import { FontBackgroundColor, FontColor, FontFamily, FontSize } from '@ckeditor/ckeditor5-font';
// import { FormatPainter } from '@ckeditor/ckeditor5-format-painter';
import { Heading, Title } from '@ckeditor/ckeditor5-heading';
import { Highlight } from '@ckeditor/ckeditor5-highlight';
import { HorizontalLine } from '@ckeditor/ckeditor5-horizontal-line';
import { HtmlEmbed } from '@ckeditor/ckeditor5-html-embed';
import {
	DataFilter,
	DataSchema,
	GeneralHtmlSupport,
	HtmlComment
} from '@ckeditor/ckeditor5-html-support';
import {
	AutoImage,
	Image,
	ImageCaption,
	ImageInsert,
	ImageResize,
	ImageStyle,
	ImageToolbar,
	ImageUpload
} from '@ckeditor/ckeditor5-image';
import { Indent, IndentBlock } from '@ckeditor/ckeditor5-indent';
import { TextPartLanguage } from '@ckeditor/ckeditor5-language';
import { AutoLink, Link, LinkImage } from '@ckeditor/ckeditor5-link';
import { List, ListProperties, TodoList } from '@ckeditor/ckeditor5-list';
import { MediaEmbed, MediaEmbedToolbar } from '@ckeditor/ckeditor5-media-embed';
import { Paragraph } from '@ckeditor/ckeditor5-paragraph';
import { PasteFromOffice } from '@ckeditor/ckeditor5-paste-from-office';
import { StandardEditingMode } from '@ckeditor/ckeditor5-restricted-editing';
import { SelectAll } from '@ckeditor/ckeditor5-select-all';
import { ShowBlocks } from '@ckeditor/ckeditor5-show-blocks';
import { SourceEditing } from '@ckeditor/ckeditor5-source-editing';
import { Style } from '@ckeditor/ckeditor5-style';
import ElementAddAttributes  from '@ckeditor/ckeditor5-engine/src/view/element';
import {
	Table,
	TableCaption,
	TableCellProperties,
	TableColumnResize,
	TableProperties,
	TableToolbar
} from '@ckeditor/ckeditor5-table';
import { TextTransformation } from '@ckeditor/ckeditor5-typing';
import { WordCount } from '@ckeditor/ckeditor5-word-count';
import Emoji from '@wwalc/ckeditor5-emoji/src/emoji';

// You can read more about extending the build with additional plugins in the "Installing plugins" guide.
// See https://ckeditor.com/docs/ckeditor5/latest/installation/plugins/installing-plugins.html for details.

class Editor extends ClassicEditor {}
Editor. builtinPlugins = [
		Alignment,
		AutoImage,
		AutoLink,
		Autoformat,
		Autosave,
		BlockQuote,
		Bold,
		CKFinder,
		CloudServices,
		Code,
		CodeBlock,
		DataFilter,
		DataSchema,
		Essentials,
		FindAndReplace,
		FontBackgroundColor,
		FontColor,
		FontFamily,
		FontSize,
		// FormatPainter,
		GeneralHtmlSupport,
		Heading,
		Highlight,
		HorizontalLine,
		HtmlComment,
		HtmlEmbed,
		Image,
		ImageCaption,
		ImageInsert,
		ImageResize,
		ImageStyle,
		ImageToolbar,
		ImageUpload,
		Indent,
		IndentBlock,
		Italic,
		Link,
		LinkImage,
		List,
		ListProperties,
		MediaEmbed,
		MediaEmbedToolbar,
		Paragraph,
		PasteFromOffice,
		SelectAll,
		ShowBlocks,
		SourceEditing,
		StandardEditingMode,
		Strikethrough,
		Style,
		Subscript,
		Superscript,
		Table,
		TableCaption,
		TableCellProperties,
		TableColumnResize,
		// TableOfContents,
		TableProperties,
		TableToolbar,
		TextPartLanguage,
		TextTransformation,
		Title,
		TodoList,
		Underline,
		UploadAdapter,
		WordCount,
		ElementAddAttributes,
		Emoji
];

Editor.EditorConfig = {	
				  emoji: [
  { name: 'grinning', text: '😀' },
  { name: 'grin', text: '😁' },
  { name: 'joy', text: '😂' },
  { name: 'rofl', text: '🤣' },
  { name: 'smile', text: '😄' },
  { name: 'sweat_smile', text: '😅' },
  { name: 'laughing', text: '😆' },
  { name: 'wink', text: '😉' },
  { name: 'blush', text: '😊' },
  { name: 'yum', text: '😋' },
  { name: 'sunglasses', text: '😎' },
  { name: 'heart_eyes', text: '😍' },
  { name: 'kissing_heart', text: '😘' },
  { name: 'thinking', text: '🤔' },
  { name: 'neutral', text: '😐' },
  { name: 'expressionless', text: '😑' },
  { name: 'no_mouth', text: '😶' },
  { name: 'smirk', text: '😏' },
  { name: 'unamused', text: '😒' },
  { name: 'relieved', text: '😌' },
  { name: 'pensive', text: '😔' },
  { name: 'confused', text: '😕' },
  { name: 'worried', text: '😟' },
  { name: 'crying', text: '😢' },
  { name: 'persevere', text: '😣' },
  { name: 'disappointed', text: '😞' },
  { name: 'fearful', text: '😨' },
  { name: 'cold_sweat', text: '😰' },
  { name: 'scream', text: '😱' },
  { name: 'angry', text: '😠' },
  { name: 'rage', text: '😡' },
  { name: 'sleepy', text: '😪' },
  { name: 'sleeping', text: '😴' },
  { name: 'mask', text: '😷' },
  { name: 'poop', text: '💩' },
  { name: 'thumbsup', text: '👍' },
  { name: 'thumbsdown', text: '👎' },
  { name: 'clap', text: '👏' },
  { name: 'pray', text: '🙏' },
  { name: 'muscle', text: '💪' },
  { name: 'eyes', text: '👀' },
  { name: 'kiss', text: '💋' },
  { name: 'heart', text: '❤️' },
  { name: 'broken_heart', text: '💔' },
  { name: 'fire', text: '🔥' },
  { name: 'ok_hand', text: '👌' },
  { name: 'wave', text: '👋' },
  { name: 'victory', text: '✌️' },
  { name: 'call_me', text: '🤙' },
  { name: 'writing', text: '✍️' },
  { name: 'check_mark', text: '✔️' },
  { name: 'cross_mark', text: '❌' },
],
	toolbar: {
			items: [
				'heading',
				'|',
				'bold',
				'italic',
				'link',
				'bulletedList',
				'numberedList',
				'|',
				'outdent',
				'indent',
				'|',
				'imageUpload',
				'blockQuote',
				'insertTable',
				'mediaEmbed',
				'undo',
				'redo',
				'emoji',
				'code',
				'codeBlock',
				'findAndReplace',
				'fontBackgroundColor',
				'fontColor',
				'fontFamily',
				'fontSize',
				// 'formatPainter',
				'highlight',
				'horizontalLine',
				'htmlEmbed',
				'imageInsert',
				'selectAll',
				'showBlocks',
				'sourceEditing',
				'restrictedEditingException',
				'strikethrough',
				'style',
				'subscript',
				'superscript',
				'tableOfContents',
				'textPartLanguage',
				'todoList',
				'underline',
				'elementAddAttributes',
			],
		},
		language: 'en',		
		mention: {
			feeds: [
				{
					marker: '@',
					feed: [
						/* See: https://ckeditor.com/docs/ckeditor5/latest/features/mentions.html */
					]
				}
			]
		},
		menuBar: {
			isVisible: true
		},		
		link: {
			addTargetToExternalLinks: true,
			defaultProtocol: 'https://',
			decorators: {
				toggleDownloadable: {
					mode: 'manual',
					label: 'Downloadable',
					attributes: {
						download: 'file'
					}
				}
			}
		},
		list: {
			properties: {
				styles: true,
				startIndex: true,
				reversed: true
			}
		},
		
	}
export default Editor;
